import React, { useEffect, useMemo } from 'react';
import {
  Button,
  Dropdown,
  Input,
  Loader,
  Tooltip,
  useToasts,
} from 'freemium-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { get, isNil } from 'lodash';
import moment from 'moment';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { blockInvalidInteger } from 'routes/utils';
import Icon from 'shared/components/Icon';
import {
  AccountInfoType,
  TenantInfoType,
  TimeUnitType,
  surveyLimitTimeUnits,
  timeUnits,
} from 'shared/types';
import {
  Center,
  ErrorField,
  FormActions,
  FormContainer,
  FormContent,
  FormField,
  FormHeader,
  FormSection,
  FreshLink,
} from '../../../../../shared/styles';
import { getText } from '../../../../../shared/text';
import { getAccountTimeFormatForGlobalSurveyLimit } from '../../../../../shared/utils';
// import { SurveyLimitInfoType } from 'shared/types';
import {
  useGetLimitResetValue,
  useGetTenantInfo,
  useSaveTenantInfo,
} from '../../hooks';
import {
  getTenantDefalutValues,
  getTenantFormValues,
  getTenantPayload,
  getTenantSchema,
} from './utils';
import { SettingsFixedDrawer } from '../../styles';

const {
  settings: { surveylimit },
} = getText('EN');

const defaultValues = getTenantDefalutValues();

// const getFrequencyAndValue = (
//   survey_limit_info?: SurveyLimitInfoType,
// ): [string, string] => {
//   if (
//     survey_limit_info?.surveys_per_day &&
//     !isNil(survey_limit_info.surveys_per_day)
//   )
//     return ['Day', `${survey_limit_info.surveys_per_day}`];
//   if (
//     survey_limit_info?.surveys_per_week &&
//     !isNil(survey_limit_info.surveys_per_week)
//   )
//     return ['Week', `${survey_limit_info.surveys_per_week}`];
//   if (
//     survey_limit_info?.surveys_per_month &&
//     !isNil(survey_limit_info.surveys_per_month)
//   )
//     return ['Month', `${survey_limit_info.surveys_per_month}`];
//   return ['', ''];
// };

type GlobalSurveyLimitProps = {
  accountInfo?: AccountInfoType;
};

const GlobalSurveyLimit = (props: GlobalSurveyLimitProps) => {
  const { accountInfo } = props;
  const [, setToast] = useToasts();
  const { t } = useTranslation();
  const { data: tenantInfo, isLoading, isError } = useGetTenantInfo();
  const { mutate: saveTenantInfo, isLoading: isLoadingTenantInfo } =
    useSaveTenantInfo({ setToast });

  const { data: limitResetDate } = useGetLimitResetValue({ setToast });

  const formattedLimitResetValue = useMemo(() => {
    if (!isNil(limitResetDate)) {
      return moment(limitResetDate?.limit_reset_on)
        .tz(accountInfo?.timezone || moment.tz.guess())
        .format(getAccountTimeFormatForGlobalSurveyLimit(accountInfo));
    }
  }, [limitResetDate]);

  const form = useForm<TenantInfoType>({
    shouldFocusError: true,
    shouldUseNativeValidation: false,
    resolver: yupResolver(getTenantSchema(tenantInfo)),
    defaultValues,
  });
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { isDirty, submitCount, errors },
  } = form;

  const setFormValue = (fieldId: any, fieldValue: any) => {
    setValue(fieldId, fieldValue, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const onSubmit = (data: TenantInfoType) => {
    const payload = getTenantPayload({ values: data });
    saveTenantInfo({ payload });
  };

  useEffect(() => {
    if (tenantInfo?.created_at) {
      reset(getTenantFormValues({ values: tenantInfo }));
    }
  }, [tenantInfo]);

  const surveys_per_month = watch('survey_limit_info.surveys_per_month');
  const surveys_per_quarter = watch('survey_limit_info.surveys_per_quarter');
  const surveys_per_semi_annual = watch(
    'survey_limit_info.surveys_per_semi_annual',
  );
  const surveys_per_year = watch('survey_limit_info.surveys_per_year');
  const frequency_month = watch(
    'contact_settings.contact_limit.frequency_month',
  );
  const frequency_week = watch('contact_settings.contact_limit.frequency_week');
  const frequency_quarter = watch(
    'contact_settings.contact_limit.frequency_quarter',
  );
  // const [surveyFrequency, surveyCount] = getFrequencyAndValue(
  //   watch('survey_limit_info') || {},
  // );

  if (isError)
    return (
      <>
        <Center minHeight="calc(100vh - 88px)">
          {t('empty_state.message.network_error')}
        </Center>
      </>
    );

  if (isLoading)
    return (
      <>
        <Center minHeight="calc(100vh - 88px)">
          <Loader />
        </Center>
      </>
    );

  const timeUnitsTranslated = React.useMemo(() => {
    return timeUnits.map((timeUnit) => ({
      ...timeUnit,
      singleName: `${t(timeUnit.singleName)}`,
    }));
  }, [timeUnits]);

  const surveyTimeUnitsTranslated = React.useMemo(() => {
    return surveyLimitTimeUnits.map((timeUnit) => ({
      ...timeUnit,
      singleName: `${t(timeUnit.singleName)}`,
    }));
  }, [surveyLimitTimeUnits]);

  return (
    <>
      <FormContainer>
        <FormHeader hideBorder>
          <div>
            <div className="heading">{t(`${surveylimit.layout.heading}`)}</div>
            <div className="sub-heading">
              {t(`${surveylimit.layout.description}`)}
            </div>
          </div>
        </FormHeader>
        <FormContent>
          <FormSection>
            <FormField className="flex-wrap">
              <Trans
                i18nKey="settings.global_survey_limit.fields.name"
                components={{
                  number: !isNil(surveys_per_month) ? (
                    <Input
                      style={{ width: '140px' }}
                      inline
                      type="number"
                      min={1}
                      max={
                        tenantInfo?.survey_limit_info?.surveys_per_month ?? 5000
                      }
                      disabled
                      onKeyDown={blockInvalidInteger}
                      isErrored={
                        submitCount > 0 &&
                        get(
                          errors,
                          ['survey_limit_info', 'surveys_per_month', 'message'],
                          false,
                        )
                      }
                      value={watch('survey_limit_info.surveys_per_month')}
                      {...register('survey_limit_info.surveys_per_month')}
                    />
                  ) : !isNil(surveys_per_quarter) ? (
                    <Input
                      style={{ width: '140px' }}
                      inline
                      type="number"
                      min={1}
                      max={
                        tenantInfo?.survey_limit_info?.surveys_per_quarter ??
                        15000
                      }
                      disabled
                      onKeyDown={blockInvalidInteger}
                      isErrored={
                        submitCount > 0 &&
                        get(
                          errors,
                          [
                            'survey_limit_info',
                            'surveys_per_quarter',
                            'message',
                          ],
                          false,
                        )
                      }
                      value={watch('survey_limit_info.surveys_per_quarter')}
                      {...register('survey_limit_info.surveys_per_quarter')}
                    />
                  ) : !isNil(surveys_per_semi_annual) ? (
                    <Input
                      style={{ width: '140px' }}
                      inline
                      type="number"
                      min={1}
                      max={
                        tenantInfo?.survey_limit_info
                          ?.surveys_per_semi_annual ?? 30000
                      }
                      disabled
                      onKeyDown={blockInvalidInteger}
                      isErrored={
                        submitCount > 0 &&
                        get(
                          errors,
                          [
                            'survey_limit_info',
                            'surveys_per_semi_annual',
                            'message',
                          ],
                          false,
                        )
                      }
                      value={watch('survey_limit_info.surveys_per_semi_annual')}
                      {...register('survey_limit_info.surveys_per_semi_annual')}
                    />
                  ) : !isNil(surveys_per_year) ? (
                    <Input
                      style={{ width: '140px' }}
                      inline
                      type="number"
                      min={1}
                      max={
                        tenantInfo?.survey_limit_info?.surveys_per_year ?? 60000
                      }
                      disabled
                      onKeyDown={blockInvalidInteger}
                      isErrored={
                        submitCount > 0 &&
                        get(
                          errors,
                          ['survey_limit_info', 'surveys_per_year', 'message'],
                          false,
                        )
                      }
                      value={watch('survey_limit_info.surveys_per_year')}
                      {...register('survey_limit_info.surveys_per_year')}
                    />
                  ) : (
                    <></>
                  ),
                  freq: (
                    <Dropdown
                      width="160px"
                      itemToString={(v: any) => v.singleName}
                      itemComponent={(v: any) => t(v.singleName)}
                      emptyItemsText={`${t('no_results_found')}`}
                      defaultSelectedItem={surveyTimeUnitsTranslated.find(
                        (tU) =>
                          tU.id ===
                            (!isNil(surveys_per_month)
                              ? 'surveys_per_month'
                              : !isNil(surveys_per_quarter)
                              ? 'surveys_per_quarter'
                              : !isNil(surveys_per_semi_annual)
                              ? 'surveys_per_semi_annual'
                              : !isNil(surveys_per_year)
                              ? 'surveys_per_year'
                              : '') || null,
                      )}
                      onChange={(option: TimeUnitType) => {
                        if (!option) return;
                        surveyTimeUnitsTranslated.forEach((timeUnit) => {
                          setFormValue(
                            `survey_limit_info.${timeUnit.id}`,
                            timeUnit.id === option.id
                              ? defaultValues.survey_limit_info?.[option.id]
                              : null,
                          );
                        });
                      }}
                      items={surveyTimeUnitsTranslated}
                    />
                  ),
                }}
              />
              <div className="max-limit">
                <Tooltip
                  label={`${t('settings.global_survey_limit.max_limit')}`}
                  position="bottom"
                >
                  <Icon fill="#12344d" width="14" height="14" glyph="info" />
                </Tooltip>
              </div>
              <div className="limitReset-field ">
                <span>{t(`${surveylimit?.fields?.limitReset?.name}`)}</span>
                <span>{formattedLimitResetValue}</span>
              </div>
            </FormField>
            {submitCount > 0 &&
              get(
                errors,
                [
                  'survey_limit_info',
                  !isNil(surveys_per_month)
                    ? 'surveys_per_month'
                    : !isNil(surveys_per_quarter)
                    ? 'surveys_per_quarter'
                    : !isNil(surveys_per_semi_annual)
                    ? 'surveys_per_semi_annual'
                    : !isNil(surveys_per_year)
                    ? 'surveys_per_year'
                    : '',
                  'message',
                ],
                false,
              ) && (
                <ErrorField>
                  {get(
                    errors,
                    [
                      'survey_limit_info',
                      !isNil(surveys_per_month)
                        ? 'surveys_per_month'
                        : !isNil(surveys_per_quarter)
                        ? 'surveys_per_quarter'
                        : !isNil(surveys_per_semi_annual)
                        ? 'surveys_per_semi_annual'
                        : !isNil(surveys_per_year)
                        ? 'surveys_per_year'
                        : '',
                      'message',
                    ],
                    '',
                  )}
                </ErrorField>
              )}
          </FormSection>
          <FormSection>
            <div className="section-name">
              <span>{t(`${surveylimit?.fields?.section1?.name}`)}</span>
              <div className="line" />
            </div>
            <FormField className="flex-wrap">
              <Trans
                i18nKey="settings.global_survey_limit.surveyed.name"
                components={{
                  number: !isNil(frequency_month) ? (
                    <Input
                      inline
                      type="number"
                      onKeyDown={blockInvalidInteger}
                      min={1}
                      max={30}
                      {...register(
                        'contact_settings.contact_limit.frequency_month',
                      )}
                      value={watch(
                        'contact_settings.contact_limit.frequency_month',
                      )}
                      isErrored={
                        submitCount > 0 &&
                        get(
                          errors,
                          [
                            'contact_settings',
                            'contact_limit',
                            'frequency_month',
                            'message',
                          ],
                          false,
                        )
                      }
                    />
                  ) : !isNil(frequency_week) ? (
                    <Input
                      inline
                      type="number"
                      onKeyDown={blockInvalidInteger}
                      min={1}
                      max={10}
                      {...register(
                        'contact_settings.contact_limit.frequency_week',
                      )}
                      value={watch(
                        'contact_settings.contact_limit.frequency_week',
                      )}
                      isErrored={
                        submitCount > 0 &&
                        get(
                          errors,
                          [
                            'contact_settings',
                            'contact_limit',
                            'frequency_week',
                            'message',
                          ],
                          false,
                        )
                      }
                    />
                  ) : !isNil(frequency_quarter) ? (
                    <Input
                      inline
                      type="number"
                      onKeyDown={blockInvalidInteger}
                      min={1}
                      max={90}
                      {...register(
                        'contact_settings.contact_limit.frequency_quarter',
                      )}
                      isErrored={
                        submitCount > 0 &&
                        get(
                          errors,
                          [
                            'contact_settings',
                            'contact_limit',
                            'frequency_quarter',
                            'message',
                          ],
                          false,
                        )
                      }
                      value={watch(
                        'contact_settings.contact_limit.frequency_quarter',
                      )}
                    />
                  ) : (
                    <></>
                  ),
                  freq: (
                    <Dropdown
                      width="160px"
                      itemToString={(v: any) => v.singleName}
                      itemComponent={(v: any) => t(v.singleName)}
                      emptyItemsText={`${t('no_results_found')}`}
                      defaultSelectedItem={timeUnitsTranslated.find(
                        (tU) =>
                          tU.id ===
                            (!isNil(frequency_month)
                              ? 'frequency_month'
                              : !isNil(frequency_week)
                              ? 'frequency_week'
                              : !isNil(frequency_quarter)
                              ? 'frequency_quarter'
                              : '') || null,
                      )}
                      onChange={(option: TimeUnitType) => {
                        if (!option) return;
                        timeUnitsTranslated.forEach((timeUnit) => {
                          setFormValue(
                            `contact_settings.contact_limit.${timeUnit.id}`,
                            timeUnit.id === option.id
                              ? defaultValues.contact_settings?.contact_limit?.[
                                  option.id
                                ]
                              : null,
                          );
                        });
                      }}
                      items={timeUnitsTranslated}
                    />
                  ),
                }}
              />
            </FormField>
            {submitCount > 0 &&
              get(
                errors,
                [
                  'contact_settings',
                  'contact_limit',
                  !isNil(frequency_month)
                    ? 'frequency_month'
                    : !isNil(frequency_week)
                    ? 'frequency_week'
                    : !isNil(frequency_quarter)
                    ? 'frequency_quarter'
                    : '',
                  'message',
                ],
                false,
              ) && (
                <ErrorField>
                  {get(
                    errors,
                    [
                      'contact_settings',
                      'contact_limit',
                      !isNil(frequency_month)
                        ? 'frequency_month'
                        : !isNil(frequency_week)
                        ? 'frequency_week'
                        : !isNil(frequency_quarter)
                        ? 'frequency_quarter'
                        : '',
                      'message',
                    ],
                    '',
                  )}
                </ErrorField>
              )}
          </FormSection>
          <FormSection>
            <div className="section-name">
              <span>{t(`${surveylimit?.fields?.section2?.name}`)}</span>
              <div className="line" />
            </div>
            <FormField className="flex-wrap">
              <Trans
                i18nKey="settings.global_survey_limit.generate.name"
                components={{
                  number: (
                    <Input
                      inline
                      type="number"
                      onKeyDown={blockInvalidInteger}
                      min={2}
                      max={24}
                      {...register('contact_settings.recipient_list_in_hours')}
                      isErrored={
                        submitCount > 0 &&
                        get(
                          errors,
                          [
                            'contact_settings',
                            'recipient_list_in_hours',
                            'message',
                          ],
                          false,
                        )
                      }
                    />
                  ),
                }}
              />
            </FormField>
            {submitCount > 0 &&
              get(
                errors,
                ['contact_settings', 'recipient_list_in_hours', 'message'],
                false,
              ) && (
                <ErrorField>
                  {get(
                    errors,
                    ['contact_settings', 'recipient_list_in_hours', 'message'],
                    '',
                  )}
                </ErrorField>
              )}
          </FormSection>
        </FormContent>
        <FormActions>
          <div className="left-pane">
            <Button
              inline
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                handleSubmit(onSubmit, (err) => console.log('ERR', err))();
              }}
              disabled={!isDirty || isLoadingTenantInfo}
            >
              {t('save')}
            </Button>
            <Button
              inline
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                reset();
              }}
              type="secondary"
              disabled={!isDirty || isLoadingTenantInfo}
            >
              {t('cancel')}
            </Button>
          </div>
        </FormActions>
      </FormContainer>
      <SettingsFixedDrawer>
        <div className="info-heading">{t(`${surveylimit.layout.heading}`)}</div>
        <div className="info-description">
          {t(`${surveylimit?.misc?.details}`)}
        </div>
        <div className="info-description">
          <FreshLink
            href="https://support.freshsurvey.io/en/support/solutions/articles/50000004073-how-can-i-set-a-global-survey-limit-to-my-account-"
            target="_blank"
            rel="noreferrer"
          >
            {t(`${surveylimit?.misc?.link}`)}
          </FreshLink>
        </div>
      </SettingsFixedDrawer>
    </>
  );
};

export default GlobalSurveyLimit;
